import apiClient from './api';
import type { PreOrder, PreOrderStatus } from '../types/catalog';
import type { PagedResponse } from '../types/common';

export interface PreOrderSearchParams {
  // Omitted = default active view (NEW + CONFIRMED)
  status?: PreOrderStatus[];
  page?: number;
  size?: number;
}

export const preOrderService = {
  // Client: turn the current user's cart for a catalog into a PreOrder (server re-reads prices)
  checkout: async (catalogId: number, phone: string): Promise<PreOrder> => {
    const response = await apiClient.post('/preorders', { catalogId, phone });
    return response.data;
  },

  // Owner: list a company's pre-orders, oldest first
  search: async (companyId: number, params?: PreOrderSearchParams): Promise<PagedResponse<PreOrder>> => {
    const response = await apiClient.get('/preorders', {
      params: {
        companyId,
        page: params?.page,
        size: params?.size,
        // Spring binds a comma-separated value to List<PreOrderStatus>
        status: params?.status?.join(','),
      },
    });
    return response.data;
  },

  // Client: the current user's own pre-orders, newest first
  searchMine: async (page = 0, size = 10): Promise<PagedResponse<PreOrder>> => {
    const response = await apiClient.get('/preorders/my', { params: { page, size } });
    return response.data;
  },

  getMine: async (id: number): Promise<PreOrder> => {
    const response = await apiClient.get(`/preorders/my/${id}`);
    return response.data;
  },

  getById: async (id: number): Promise<PreOrder> => {
    const response = await apiClient.get(`/preorders/${id}`);
    return response.data;
  },

  confirm: async (id: number): Promise<PreOrder> => {
    const response = await apiClient.post(`/preorders/${id}/confirm`);
    return response.data;
  },

  // Mark as fulfilled without creating any Activity (fulfilment happens outside the CRM)
  closeWithoutActivity: async (id: number): Promise<PreOrder> => {
    const response = await apiClient.post(`/preorders/${id}/close-without-activity`);
    return response.data;
  },

  // Link the Activity Groups created from this pre-order and mark it FULFILLED
  fulfill: async (id: number, activityGroupIds: number[]): Promise<PreOrder> => {
    const response = await apiClient.post(`/preorders/${id}/fulfill`, { activityGroupIds });
    return response.data;
  },

  // Owner correction: quantities/removal only (full desired item set)
  updateItems: async (id: number, items: { productId: number; quantity: number }[]): Promise<PreOrder> => {
    const response = await apiClient.put(`/preorders/${id}/items`, { items });
    return response.data;
  },

  cancel: async (id: number): Promise<PreOrder> => {
    const response = await apiClient.post(`/preorders/${id}/cancel`);
    return response.data;
  },
};
